import { CommonModule, DatePipe } from '@angular/common';
import { Component, ElementRef, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Observable, Subject, catchError, debounceTime, forkJoin, map, of, switchMap, takeUntil } from 'rxjs';
import { DecisaoPrecoVinculoEnum } from '@shared/enums/compras/decisao-preco-vinculo.enum';
import { DecisaoQuantidadeVinculoEnum } from '@shared/enums/compras/decisao-quantidade-vinculo.enum';
import { TipoRegraConfrontoEnum } from '@shared/enums/compras/tipo-regra-confronto.enum';
import {
  classeBadgeStatusNFeEntrada,
  rotuloStatusNFeEntrada,
} from '@shared/helpers/nota-fiscal-entrada-status.helper';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { MaskService } from '@shared/services/mask.service';
import { CompraNFeEntradaVinculoService } from '@shared/services/apis/compras/compra-nfe-entrada-vinculo.service';
import { EtapasKanbanService } from '@shared/services/apis/compras/etapas-kanban.service';
import { NotaFiscalEntradaService } from '@shared/services/apis/orquestrador-compras/nota-fiscal-entrada.service';
import { PessoaLookupService } from '@shared/services/apis/pessoa/pessoa-lookup.service';
import { AutomacaoService } from '@shared/services/apis/compras/automacao.service';
import { TipoOcorrenciaCompraEnum } from '@shared/enums/compras/tipo-ocorrencia-compra.enum';
import { ListPessoaIdsRequest } from '@shared/services/requests/pessoa/list-pessoa-ids-request';
import { CompraNFeEntradaPedidosElegiveisRequest } from '@shared/services/requests/compras/compra-nfe-entrada-pedidos-elegiveis.request';
import { NotaFiscalEntradaVincularItensPedidoRequest } from '@shared/services/requests/orquestrador-compras/nota-fiscal-entrada-vincular-itens-pedido.request';
import { CompraNFeEntradaPedidosElegiveisResponse } from '@shared/services/responses/compras/compra-nfe-entrada-pedidos-elegiveis.response';
import { PedidoElegivelVinculoStruct } from '@shared/services/structs/compras/pedido-elegivel-vinculo.struct';
import { VinculoPedidoNotaItemStruct } from '@shared/services/structs/orquestrador-compras/vinculo-pedido-nota-item.struct';
import { VinculoPedidoNotaStruct } from '@shared/services/structs/orquestrador-compras/vinculo-pedido-nota.struct';
import { formatCodigoCompra } from '../card-edit-dialog/card-edit-dialog.types';
import { VinculoPedidoNfDialogData, VinculoPedidoNfDialogResult } from './vinculo-pedido-nf-dialog.data';
import {
  CalculoVinculoItem,
  DecisaoExcesso,
  EstadoItemVinculo,
  FiltroVinculo,
  ItemVinculoVm,
  OpcaoItemPedido,
  TOLERANCIA_PADRAO,
  ToleranciasVinculo,
  UsoSaldoOutros,
  arred4,
  calcularUsoSaldoOutros,
  calcularVinculoItem,
  estadoInicialItem,
  fmtMoeda,
  montarItemVm,
  montarItensRequest,
  montarOpcoes,
  montarOpcoesBloqueadas,
  normalizaCodigoPedido,
  resetarDecisoes,
} from './vinculo-pedido-nf-dialog.types';

const TAMANHO_BUSCA = 20;
const TAMANHO_BUSCA_PRODUTO = 50;
const TAMANHO_MAXIMIZADO = { largura: '100vw', altura: '100vh' };
const TAMANHO_RESTAURADO = { largura: '96vw', altura: '92vh' };

/**
 * Modal de vínculo NF × pedido de compra:
 * para cada item da NF-e (já associada a produto na Central), o usuário confirma a qual item de
 * pedido ele pertence, com confronto de quantidade e de preço e decisões. A gravação é uma só, no Concluir;
 * "Vincular" por item só confirma na tela.
 *
 * Aberto direto pela Gaveta, sem pedido pré-escolhido, quando todos os itens da nota já
 * têm produto associado; a Gaveta passa o contexto que já carregou para decidir. Os pedidos vêm
 * da sugestão do XML (`xPed`), do fornecedor ou produtos da nota e da busca; item sem sugestão
 * começa em "Escolher item do pedido".
 * Mesma moldura da associação de itens da Central (abre em 96vw × 92vh; o botão do cabeçalho
 * maximiza para a tela cheia e volta):
 *
 * ```ts
 * this.dialog.open<VinculoPedidoNfDialogComponent, VinculoPedidoNfDialogData, VinculoPedidoNfDialogResult>(
 *   VinculoPedidoNfDialogComponent, {
 *     width: '96vw', height: '92vh', maxWidth: '100vw', maxHeight: '100vh',
 *     disableClose: true, autoFocus: false, panelClass: 'vinculo-pedido-nf-dialog',
 *     data: { idNFeEntrada, nomeFornecedor, contexto },
 *   }).afterClosed().subscribe(resultado => { if (resultado?.vinculado) { ... } });
 * ```
 *
 * Fecha com `undefined` ao cancelar.
 */
@Component({
  selector: 'app-vinculo-pedido-nf-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    FormsModule,
    NgxCurrencyDirective,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    AbntPipe,
    AlertCardComponent,
  ],
  templateUrl: './vinculo-pedido-nf-dialog.component.html',
  styleUrls: ['./vinculo-pedido-nf-dialog.component.css'],
})
export class VinculoPedidoNfDialogComponent implements OnInit, OnDestroy {
  readonly classeBadgeStatus = classeBadgeStatusNFeEntrada;
  readonly rotuloStatus = rotuloStatusNFeEntrada;
  /** Para marcar no template a opção escolhida nos cartões de decisão. */
  readonly DecisaoQuantidade = DecisaoQuantidadeVinculoEnum;
  readonly DecisaoPreco = DecisaoPrecoVinculoEnum;
  /** Quantidade a relacionar: casas decimais de quantidade do usuário, sem negativo. */
  readonly quantidadeMask = this.maskService.getDynamicDecimalMask(null, false);
  readonly chips: { chave: FiltroVinculo; rotulo: string }[] = [
    { chave: 'todos', rotulo: 'Todos' },
    { chave: 'pendentes', rotulo: 'Pendentes' },
    { chave: 'vinculados', rotulo: 'Vinculados' },
  ];

  carregando = true;
  salvando = false;
  isMaximizado = false;
  filtro: FiltroVinculo = 'todos';
  nota: VinculoPedidoNotaStruct | null = null;

  /** Itens visíveis no filtro atual, já com o view-model calculado. */
  itensVm: ItemVinculoVm[] = [];
  totalItens = 0;
  totalVinculados = 0;

  private itens: VinculoPedidoNotaItemStruct[] = [];
  private pedidos: PedidoElegivelVinculoStruct[] = [];
  /** Itens de pedidos elegíveis — os únicos que podem ser sugeridos, marcados e alocados. */
  private opcoes: OpcaoItemPedido[] = [];
  /** Itens de pedidos bloqueados pela regra de movimentação: só exibidos, desabilitados. */
  private bloqueadas: OpcaoItemPedido[] = [];
  private readonly estados = new Map<number, EstadoItemVinculo>();
  /** Tolerâncias de preço e quantidade da etapa `faturado`; até carregar (ou sem a etapa), o padrão do backend. */
  private tolerancias: ToleranciasVinculo = {
    precoPercentual: TOLERANCIA_PADRAO,
    quantidadePercentual: TOLERANCIA_PADRAO,
    regraPreco: TipoRegraConfrontoEnum.Avisar,
    regraQuantidade: TipoRegraConfrontoEnum.Avisar,
  };
  /** Nome do fornecedor de cada pedido carregado, por `idFornecedor` (lookup de pessoas). */
  private readonly nomesFornecedor = new Map<number, string>();
  /** `idFornecedor` já pedidos ao lookup (em andamento ou resolvidos), para não pedir de novo. */
  private readonly fornecedoresSolicitados = new Set<number>();
  private flashId: number | null = null;
  private buscandoId: number | null = null;
  /** Erros das buscas iniciais, reunidos num aviso único ao fim da carga. */
  private readonly errosCarga: string[] = [];
  private readonly timers: ReturnType<typeof setTimeout>[] = [];
  private readonly busca$ = new Subject<{ idItem: number; termo: string }>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: VinculoPedidoNfDialogData,
    private dialogRef: MatDialogRef<VinculoPedidoNfDialogComponent, VinculoPedidoNfDialogResult>,
    private notaFiscalEntradaService: NotaFiscalEntradaService,
    private compraNFeEntradaVinculoService: CompraNFeEntradaVinculoService,
    private etapasKanbanService: EtapasKanbanService,
    private pessoaLookupService: PessoaLookupService,
    private alertService: AlertService,
    private automacaoService: AutomacaoService,
    private maskService: MaskService,
    private elementRef: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.inscreverBuscaPedidos();
    this.carregar();
  }

  ngOnDestroy(): void {
    this.timers.forEach(t => clearTimeout(t));
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ------------------------------------------------------------------------------------------
  // Leitura para o template
  // ------------------------------------------------------------------------------------------

  get numeroNota(): string {
    if (!this.nota) return '—';
    const numero = this.nota.numero ?? '—';
    return this.nota.serie ? `${numero}/${this.nota.serie}` : numero;
  }

  get nomeEmitente(): string {
    return this.nota?.nomeEmitente || this.data.nomeFornecedor || 'Fornecedor não identificado';
  }

  get valorTotalNota(): string {
    return fmtMoeda(this.nota?.valorTotal);
  }

  get percentualVinculado(): number {
    return this.totalItens ? Math.round((this.totalVinculados / this.totalItens) * 100) : 0;
  }

  get podeConcluir(): boolean {
    return !this.carregando && this.totalItens > 0 && this.totalVinculados === this.totalItens;
  }

  /** Motivo do Concluir desabilitado (tooltip); vazio = liberado. */
  get motivoBloqueioConcluir(): string {
    if (this.carregando || this.salvando || this.podeConcluir) return '';
    const pendentes = this.totalItens - this.totalVinculados;
    return pendentes === 1 ? 'Falta vincular 1 item da nota.' : `Faltam vincular ${pendentes} itens da nota.`;
  }

  // ------------------------------------------------------------------------------------------
  // Moldura
  // ------------------------------------------------------------------------------------------

  alternarTamanho(): void {
    this.isMaximizado = !this.isMaximizado;
    const tamanho = this.isMaximizado ? TAMANHO_MAXIMIZADO : TAMANHO_RESTAURADO;
    this.dialogRef.updateSize(tamanho.largura, tamanho.altura);
    // Tela cheia sem cantos arredondados, como a associação de itens da Central.
    if (this.isMaximizado) this.dialogRef.addPanelClass('associacao-dialog-maximizado');
    else this.dialogRef.removePanelClass('associacao-dialog-maximizado');
    // Clique deixa o mat-icon-button com foco (e a sombra cinza). Solta o foco
    // para o state layer sumir; teclado continua com :focus-visible.
    (document.activeElement as HTMLElement | null)?.blur?.();
  }

  cancelar(): void {
    if (this.salvando) return;
    this.dialogRef.close(undefined);
  }

  filtrar(filtro: FiltroVinculo): void {
    this.filtro = filtro;
    this.recalcular();
  }

  // ------------------------------------------------------------------------------------------
  // Ações por item
  // ------------------------------------------------------------------------------------------

  alternarItem(idItem: number): void {
    const estado = this.estados.get(idItem);
    if (!estado) return;
    estado.aberto = !estado.aberto;
    this.recalcular();
  }

  /** "Vincular": confirma na tela, recolhe o item e leva o foco ao próximo pendente. */
  confirmarItem(idItem: number): void {
    const item = this.itemPorId(idItem);
    const estado = this.estados.get(idItem);
    if (!item || !estado) return;
    // Pronto também exige a decisão de preço quando algum preço passou da tolerância.
    if (!this.calcularItem(item, estado).pronto) return;
    estado.confirmado = true;
    estado.aberto = false;
    this.recalcular();
    this.focarProximoVinculo(idItem);
  }

  abrirSelecao(idItem: number): void {
    const estado = this.estados.get(idItem);
    if (!estado) return;
    estado.selecionando = true;
    this.recalcular();
  }

  fecharSelecao(idItem: number): void {
    const estado = this.estados.get(idItem);
    if (!estado) return;
    estado.selecionando = false;
    this.recalcular();
  }

  pesquisarItensPedido(idItem: number, termo: string): void {
    const estado = this.estados.get(idItem);
    if (!estado) return;
    estado.busca = termo;
    this.recalcular();
    this.busca$.next({ idItem, termo: termo.trim() });
  }

  alternarCandidato(idItem: number, chave: string): void {
    // Item de pedido bloqueado (inelegível) aparece na lista, mas nunca pode ser marcado.
    if (!this.opcoes.some(o => o.chave === chave)) return;
    this.alterarSelecao(idItem, selecao =>
      selecao.includes(chave) ? selecao.filter(c => c !== chave) : [...selecao, chave], chave);
  }

  desvincular(idItem: number, chave: string): void {
    this.alterarSelecao(idItem, selecao => selecao.filter(c => c !== chave), chave);
  }

  alterarAlocacao(idItem: number, chave: string, valor: number | null): void {
    const estado = this.estados.get(idItem);
    if (!estado) return;
    estado.alocEdit = { ...estado.alocEdit, [chave]: valor };
    resetarDecisoes(estado);
    this.recalcular();
  }

  /** "Completar": relaciona o máximo possível deste item, sem passar do saldo nem da nota. */
  completarAlocacao(idItem: number, chave: string): void {
    const item = this.itemPorId(idItem);
    const estado = this.estados.get(idItem);
    if (!item || !estado) return;
    const calc = this.calcularItem(item, estado);
    const opcao = calc.escolhidos.find(o => o.chave === chave);
    if (!opcao) return;
    const outros = calc.escolhidos
      .filter(o => o.chave !== chave)
      .reduce((t, o) => t + (calc.alocacao.aloc[o.chave] ?? 0), 0);
    // Até o saldo que os outros itens da nota deixam neste item do pedido.
    const valor = Math.max(0, Math.min(calc.alocacao.disponivel[chave], arred4(calc.qtdEstoque - outros)));
    estado.alocEdit = { ...estado.alocEdit, [chave]: valor };
    resetarDecisoes(estado);
    this.recalcular();
  }

  aceitarExcesso(idItem: number): void {
    this.decidirExcesso(idItem, DecisaoQuantidadeVinculoEnum.AceitarAjustarSaldo);
  }

  marcarDivergencia(idItem: number): void {
    this.decidirExcesso(idItem, DecisaoQuantidadeVinculoEnum.MarcarDivergencia);
  }

  aceitarAjustePreco(idItem: number): void {
    this.decidirPreco(idItem, DecisaoPrecoVinculoEnum.AceitarAjustarPreco);
  }

  marcarDivergenciaPreco(idItem: number): void {
    this.decidirPreco(idItem, DecisaoPrecoVinculoEnum.MarcarDivergencia);
  }

  receberParcial(idItem: number): void {
    const estado = this.estados.get(idItem);
    if (!estado) return;
    estado.parcialOk = true;
    this.recalcular();
  }

  // ------------------------------------------------------------------------------------------
  // Concluir
  // ------------------------------------------------------------------------------------------

  concluir(): void {
    if (!this.podeConcluir || this.salvando) return;
    const request: NotaFiscalEntradaVincularItensPedidoRequest = {
      idNFeEntrada: this.data.idNFeEntrada,
      idsVinculoSubstituir: this.data.idsVinculoSubstituir ?? [],
      itens: montarItensRequest(this.itens, this.estados, this.opcoes, this.nota?.idPessoaEmitente ?? null, this.tolerancias),
      idsUsuarioPerfil: [],
    };

    this.salvando = true;
    // O vínculo notifica "NF vinculada" e, com divergência marcada, "Divergência registrada": expande os perfis dos dois.
    const idsUsuarioPerfil$ = forkJoin([
      this.automacaoService.idsUsuarioDosPerfis(TipoOcorrenciaCompraEnum.VinculoNFeEntrada),
      this.automacaoService.idsUsuarioDosPerfis(TipoOcorrenciaCompraEnum.DivergenciaRegistrada),
    ]).pipe(map(([vinculou, divergencia]) => [...new Set([...vinculou, ...divergencia])]));
    this.notaFiscalEntradaService.confrontarItensPedido(request)
      .pipe(switchMap(confronto => confronto.isError
        ? of({ ...confronto, compras: [], avisoDescricao: null })
        : idsUsuarioPerfil$.pipe(switchMap(idsUsuarioPerfil =>
          this.notaFiscalEntradaService.vincularItensPedido({ ...request, idsUsuarioPerfil })))))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.salvando = false;
          if (response.isError) {
            this.alertService.show('Atenção', response.errorDescription, AlertType.warning);
            if (response.compras?.length) this.dialogRef.close({ vinculado: true, compras: response.compras });
            return;
          }
          // Split: o saldo não faturado virou pedido(s) novo(s) na etapa de origem do pai.
          const filhos = (response.compras ?? [])
            .filter(c => c.isFilhoSplit)
            .map(c => formatCodigoCompra(c.codigo) || `#${c.idCompra}`);
          const mensagem = !filhos.length
            ? 'Vínculo com a NF atualizado.'
            : filhos.length === 1
              ? `Vínculo concluído. Saldo não faturado gerou o pedido ${filhos[0]}.`
              : `Vínculo concluído. Saldo não faturado gerou os pedidos ${filhos.slice(0, -1).join(', ')} e ${filhos[filhos.length - 1]}.`;
          this.alertService.show('Sucesso', mensagem, AlertType.success);
          if (response.avisoDescricao) this.alertService.show('Atenção', response.avisoDescricao, AlertType.warning);
          this.dialogRef.close({ vinculado: true, compras: response.compras ?? [] });
        },
        error: () => {
          this.salvando = false;
          this.alertService.show('Erro', 'Não foi possível vincular os itens da NF-e ao pedido.', AlertType.error);
        },
      });
  }

  // ------------------------------------------------------------------------------------------
  // Internos
  // ------------------------------------------------------------------------------------------

  /**
   * A nota e os itens chegam prontos da Gaveta (`data.contexto`). Aqui só se carregam os pedidos
   * iniciais, em paralelo: uma busca por `xPed` distinto dos itens (sugestão do XML) e uma busca
   * pelo fornecedor (emitente da nota) OU pelos produtos de todos os itens, que traz a faixa de cada
   * pedido (sem os dois, é a primeira página sem filtro). O item cujo `xPed` não virou sugestão cai
   * na sugestão pelo produto, já coberta por essa busca. Falha em qualquer uma avisa e segue com o
   * que carregou: o usuário ainda pode buscar. Junto vêm as tolerâncias de preço e quantidade da etapa `faturado`.
   */
  private carregar(): void {
    this.nota = this.data.contexto.nota;
    this.itens = [...(this.data.contexto.itens ?? [])].sort((a, b) => a.numeroItem - b.numeroItem);
    this.itens.forEach(item => this.estados.set(item.idNFeEntradaItem, estadoInicialItem()));

    const xPeds = [...new Set(this.itens
      .map(item => normalizaCodigoPedido(item.numeroPedidoCompra))
      .filter(x => !!x))];
    const buscas = [
      ...(this.data.idsCompra?.length ? [this.buscarPedidosIniciais({ idsCompra: this.data.idsCompra })] : []),
      ...xPeds.map(xPed => this.buscarPedidosIniciais({ pesquisar: xPed })),
      this.buscarPedidosIniciais({
        idFornecedor: this.nota?.idPessoaEmitente ?? null,
        idsProduto: this.idsProdutoDistintos(this.itens),
      }),
    ];

    forkJoin([forkJoin(buscas), this.buscarTolerancias()])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([respostas, tolerancias]) => {
        this.tolerancias = tolerancias.tolerancias;
        if (tolerancias.erro) this.errosCarga.push(tolerancias.erro);
        this.mesclarRespostas(respostas);
        this.carregarNomesFornecedor();
        if (this.errosCarga.length) {
          this.alertService.show('Atenção', [...new Set(this.errosCarga)].join(' '), AlertType.warning);
        }
        this.carregando = false;
        const restaurados = this.restaurarVinculoGravado();
        if (this.data.idsCompra?.length) {
          this.itens.forEach(item => {
            if (restaurados.has(item.idNFeEntradaItem)) return;
            const estado = this.estados.get(item.idNFeEntradaItem)!;
            const candidatas = this.opcoes.filter(o => this.data.idsCompra!.includes(o.idCompra) && o.idProduto === item.idProduto);
            if (candidatas.length === 1) estado.selecao = [candidatas[0].chave];
          });
        }
        this.recalcular();
      });
  }

  /**
   * Gerenciar vínculo / Trocar NF (`idsVinculoSubstituir`): cada item da nota volta exatamente como
   * foi gravado nos vínculos substituídos — itens de pedido pelo `idCompraItem` (mesmo com itens
   * repetidos do mesmo produto), quantidades como alocação manual e as decisões de excesso e de preço. As linhas
   * são casadas pelo `idNFeEntradaItem` = `idNFeEntradaItem`; linha cujo item de pedido não
   * está entre os elegíveis carregados fica de fora. Devolve os itens restaurados; os demais seguem
   * a pré-seleção por produto.
   */
  private restaurarVinculoGravado(): Set<number> {
    const restaurados = new Set<number>();
    const idsSubstituir = this.data.idsVinculoSubstituir ?? [];
    if (!idsSubstituir.length) return restaurados;
    const linhas = (this.data.contexto.vinculosPedido ?? [])
      .filter(v => idsSubstituir.includes(v.idCompraNFeEntradaVinculo))
      .flatMap(v => v.itens ?? []);
    this.itens.forEach(item => {
      const doItem = linhas.filter(l => l.idNFeEntradaItem === item.idNFeEntradaItem
        && this.opcoes.some(o => o.idCompraItem === l.idCompraItem));
      const estado = this.estados.get(item.idNFeEntradaItem);
      if (!doItem.length || !estado) return;
      estado.selecao = doItem.map(l => String(l.idCompraItem));
      estado.alocEdit = Object.fromEntries(doItem.map(l => [String(l.idCompraItem), arred4(l.quantidadeVinculada)]));
      estado.decisaoExcesso = doItem.map(l => l.idDecisaoQuantidadeVinculo)
        .find((d): d is DecisaoExcesso => d === DecisaoQuantidadeVinculoEnum.AceitarAjustarSaldo
          || d === DecisaoQuantidadeVinculoEnum.MarcarDivergencia) ?? null;
      estado.decisaoPreco = doItem.map(l => l.idDecisaoPrecoVinculo)
        .find((d): d is DecisaoPrecoVinculoEnum => d === DecisaoPrecoVinculoEnum.AceitarAjustarPreco
          || d === DecisaoPrecoVinculoEnum.MarcarDivergencia) ?? null;
      estado.isRestaurado = true;
      restaurados.add(item.idNFeEntradaItem);
    });
    return restaurados;
  }

  private mesclarRespostas(respostas: { lista: PedidoElegivelVinculoStruct[]; erro: string | null }[]): void {
    respostas.forEach(resposta => {
      if (resposta.erro) this.errosCarga.push(resposta.erro);
      else this.mesclarPedidos(resposta.lista);
    });
  }

  private idsProdutoDistintos(itens: VinculoPedidoNotaItemStruct[]): number[] {
    return [...new Set(itens.map(item => item.idProduto).filter((id): id is number => id != null))];
  }

  /**
   * Uma página dos elegíveis; nunca falha — o erro volta como texto para o aviso único. Sem
   * fornecedor nem produtos, é a página sem filtro.
   */
  private buscarPedidosIniciais(
    filtro: { pesquisar?: string; idsProduto?: number[]; idsCompra?: number[]; idFornecedor?: number | null },
  ): Observable<{ lista: PedidoElegivelVinculoStruct[]; erro: string | null }> {
    const request = new CompraNFeEntradaPedidosElegiveisRequest();
    request.pesquisar = filtro.pesquisar ?? null;
    request.idsProduto = filtro.idsProduto?.length ? filtro.idsProduto : null;
    request.idsCompra = filtro.idsCompra ?? null;
    request.idFornecedor = filtro.idFornecedor ?? null;
    request.idsVinculoSubstituir = this.data.idsVinculoSubstituir ?? [];
    request.pagina = 0;
    request.tamanhoPagina = request.idsProduto || request.idFornecedor != null ? TAMANHO_BUSCA_PRODUTO : TAMANHO_BUSCA;
    return this.compraNFeEntradaVinculoService.pedidosElegiveis(request).pipe(
      map(response => response.isError
        ? { lista: [], erro: response.errorDescription || 'Não foi possível carregar os pedidos de compra.' }
        : { lista: response.lista ?? [], erro: null }),
      catchError(() => of({ lista: [], erro: 'Não foi possível carregar os pedidos de compra.' })),
    );
  }

  /**
   * Tolerâncias de preço e quantidade da etapa `faturado`, da lista de etapas do Kanban de Compras
   * (nenhum chamador do modal tem as etapas à mão). Percentual nulo vale 5% e regra nula vale Avisar,
   * como no backend. Nunca falha: sem a lista, segue com o padrão e o erro vai para o aviso único da carga.
   */
  private buscarTolerancias(): Observable<{ tolerancias: ToleranciasVinculo; erro: string | null }> {
    const padrao: ToleranciasVinculo = {
      precoPercentual: TOLERANCIA_PADRAO,
      quantidadePercentual: TOLERANCIA_PADRAO,
      regraPreco: TipoRegraConfrontoEnum.Avisar,
      regraQuantidade: TipoRegraConfrontoEnum.Avisar,
    };
    const erro = 'Não foi possível carregar as tolerâncias da etapa Pedido Faturado; '
      + `o vínculo usa ${TOLERANCIA_PADRAO}% para preço e quantidade.`;
    return this.etapasKanbanService.getAll('', false).pipe(
      map(response => {
        if (response.isError) return { tolerancias: padrao, erro };
        // Como o backend, só a etapa ativa: inativa (ou ausente) vale o padrão.
        const faturado = (response.listaEtapas ?? []).find(e => e.chave === 'faturado' && e.isAtivo);
        return {
          tolerancias: {
            precoPercentual: faturado?.toleranciaPrecoPercentual ?? TOLERANCIA_PADRAO,
            quantidadePercentual: faturado?.toleranciaQuantidadePercentual ?? TOLERANCIA_PADRAO,
            regraPreco: faturado?.idRegraToleranciaPreco === TipoRegraConfrontoEnum.Proibir
              ? TipoRegraConfrontoEnum.Proibir
              : TipoRegraConfrontoEnum.Avisar,
            regraQuantidade: faturado?.idRegraToleranciaQuantidade === TipoRegraConfrontoEnum.Proibir
              ? TipoRegraConfrontoEnum.Proibir
              : TipoRegraConfrontoEnum.Avisar,
          },
          erro: null,
        };
      }),
      catchError(() => of({ tolerancias: padrao, erro })),
    );
  }

  /**
   * Busca de itens de pedido: com 2+ caracteres, pergunta aos elegíveis do servidor (cancelando a
   * busca anterior) e mescla os pedidos novos no conjunto carregado — a lista em si é filtrada no
   * cliente sobre tudo o que já foi carregado.
   */
  private inscreverBuscaPedidos(): void {
    this.busca$
      .pipe(
        debounceTime(350),
        switchMap(({ idItem, termo }): Observable<CompraNFeEntradaPedidosElegiveisResponse | null> => {
          if (termo.length < 2) {
            this.definirBuscando(null);
            return of(null);
          }
          this.definirBuscando(idItem);
          const request = new CompraNFeEntradaPedidosElegiveisRequest();
          request.pesquisar = termo;
          request.idsVinculoSubstituir = this.data.idsVinculoSubstituir ?? [];
          request.pagina = 0;
          request.tamanhoPagina = TAMANHO_BUSCA;
          return this.compraNFeEntradaVinculoService.pedidosElegiveis(request).pipe(
            catchError(() => {
              this.alertService.show('Erro', 'Não foi possível pesquisar os pedidos de compra.', AlertType.error);
              return of(null);
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(response => {
        this.buscandoId = null;
        if (response?.isError) {
          this.alertService.show('Atenção', response.errorDescription, AlertType.warning);
        } else if (response) {
          this.mesclarPedidos(response.lista ?? []);
          this.carregarNomesFornecedor();
        }
        this.recalcular();
      });
  }

  private definirBuscando(idItem: number | null): void {
    if (this.buscandoId === idItem) return;
    this.buscandoId = idItem;
    this.recalcular();
  }

  /**
   * Acrescenta pedidos ao conjunto carregado, sem repetir `idCompra`. Pedido que já estava carregado
   * é atualizado no lugar com a versão nova (saldo e elegibilidade podem ter mudado); a faixa já
   * conhecida fica quando a versão nova vem sem ela (busca por termo, `xPed` ou `idsCompra`). Quem
   * chama recalcula.
   */
  private mesclarPedidos(novos: PedidoElegivelVinculoStruct[]): void {
    if (!novos.length) return;
    const selecaoAntes = this.selecoesEfetivas();
    const porId = new Map(novos.map(p => [p.idCompra, p]));
    const conhecidos = new Set(this.pedidos.map(p => p.idCompra));
    this.pedidos = [
      ...this.pedidos.map(p => {
        const novo = porId.get(p.idCompra);
        return novo ? { ...novo, faixaSugestao: novo.faixaSugestao ?? p.faixaSugestao } : p;
      }),
      ...novos.filter(p => !conhecidos.has(p.idCompra)),
    ];
    this.opcoes = montarOpcoes(this.pedidos);
    this.bloqueadas = montarOpcoesBloqueadas(this.pedidos);
    this.descartarSelecoesBloqueadas(selecaoAntes);
  }

  /**
   * Nomes dos fornecedores dos pedidos carregados, como o seletor de NF: numa chamada, só os
   * `idFornecedor` ainda não pedidos; nome = fantasia ou razão social. Falha avisa e não quebra o modal: os
   * ids voltam a poder ser pedidos e, até lá, o pedido mostra o fallback com o id.
   */
  private carregarNomesFornecedor(): void {
    const ids = [...new Set(this.pedidos
      .map(p => p.idFornecedor)
      .filter((id): id is number => id != null && !this.fornecedoresSolicitados.has(id)))];
    if (!ids.length) return;
    ids.forEach(id => this.fornecedoresSolicitados.add(id));
    const request = new ListPessoaIdsRequest();
    request.listIdPessoa = ids;
    this.pessoaLookupService.getListPessoaIds(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: resposta => {
          if (resposta.isError) {
            ids.forEach(id => this.fornecedoresSolicitados.delete(id));
            this.alertService.show('Erro',
              resposta.errorDescription || 'Não foi possível carregar os fornecedores dos pedidos.', AlertType.error);
            return;
          }
          (resposta.listaPessoa ?? []).forEach(pessoa => {
            const nome = pessoa.nomeFantasia || pessoa.nome;
            if (ids.includes(pessoa.idPessoa) && nome) this.nomesFornecedor.set(pessoa.idPessoa, nome);
          });
          this.recalcular();
        },
        error: () => {
          ids.forEach(id => this.fornecedoresSolicitados.delete(id));
          this.alertService.show('Erro', 'Não foi possível carregar os fornecedores dos pedidos.', AlertType.error);
        },
      });
  }

  /** Seleção em vigor de cada item (a do usuário ou a sugestão do XML), só com itens elegíveis. */
  private selecoesEfetivas(): Map<number, string[]> {
    const mapa = new Map<number, string[]>();
    const usos = this.calcularUsos();
    this.itens.forEach(item => {
      const estado = this.estados.get(item.idNFeEntradaItem);
      if (!estado) return;
      const calc = calcularVinculoItem(item, estado, this.opcoes, this.nota?.idPessoaEmitente ?? null, this.tolerancias,
        usos.get(item.idNFeEntradaItem)!);
      mapa.set(item.idNFeEntradaItem, calc.escolhidos.map(o => o.chave));
    });
    return mapa;
  }

  /**
   * Item de pedido que ficou inelegível depois de uma recarga nunca continua selecionado: sai da
   * seleção (explícita ou sugerida) e as decisões e a confirmação do item da nota são refeitas.
   */
  private descartarSelecoesBloqueadas(selecaoAntes: Map<number, string[]>): void {
    const elegiveis = new Set(this.opcoes.map(o => o.chave));
    this.itens.forEach(item => {
      const estado = this.estados.get(item.idNFeEntradaItem);
      const antes = selecaoAntes.get(item.idNFeEntradaItem) ?? [];
      const perdidas = antes.filter(chave => !elegiveis.has(chave));
      if (!estado || !perdidas.length) return;
      estado.selecao = antes.filter(chave => elegiveis.has(chave));
      const alocEdit = { ...estado.alocEdit };
      perdidas.forEach(chave => delete alocEdit[chave]);
      estado.alocEdit = alocEdit;
      resetarDecisoes(estado);
    });
  }

  private alterarSelecao(idItem: number, alterar: (selecao: string[]) => string[], chave: string): void {
    const item = this.itemPorId(idItem);
    const estado = this.estados.get(idItem);
    if (!item || !estado) return;
    const atual = this.calcularItem(item, estado).selecao;
    estado.selecao = alterar(atual);
    // Mexeu na seleção: deixa de ser o vínculo gravado e a pré-seleção pelo produto volta a valer.
    estado.isRestaurado = false;
    const alocEdit = { ...estado.alocEdit };
    delete alocEdit[chave];
    estado.alocEdit = alocEdit;
    resetarDecisoes(estado);
    this.recalcular();
  }

  private decidirExcesso(idItem: number, decisao: DecisaoExcesso): void {
    const estado = this.estados.get(idItem);
    if (!estado) return;
    estado.decisaoExcesso = decisao;
    this.recalcular();
  }

  private decidirPreco(idItem: number, decisao: DecisaoPrecoVinculoEnum): void {
    const estado = this.estados.get(idItem);
    if (!estado) return;
    estado.decisaoPreco = decisao;
    this.recalcular();
  }

  /**
   * Porte do `focarProximoVinculo` do protótipo: depois de vincular, abre o próximo pendente
   * (na ordem da lista, dando a volta), rola até ele e o destaca por 1,5 s.
   */
  private focarProximoVinculo(deId: number): void {
    this.agendar(() => {
      const linhas = this.itensVm;
      const i = linhas.findIndex(l => l.id === deId);
      const fila = i < 0 ? linhas : [...linhas.slice(i + 1), ...linhas.slice(0, i)];
      const proximo = fila.find(l => !l.resolvido);
      if (!proximo) return;
      const estado = this.estados.get(proximo.id);
      if (estado) estado.aberto = true;
      this.flashId = proximo.id;
      this.recalcular();
      this.agendar(() => {
        const el = this.elementRef.nativeElement.querySelector<HTMLElement>(`#${proximo.ancora}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 40);
      this.agendar(() => {
        if (this.flashId !== proximo.id) return;
        this.flashId = null;
        this.recalcular();
      }, 1500);
    }, 240);
  }

  private agendar(fn: () => void, ms: number): void {
    this.timers.push(setTimeout(fn, ms));
  }

  private itemPorId(idItem: number): VinculoPedidoNotaItemStruct | undefined {
    return this.itens.find(i => i.idNFeEntradaItem === idItem);
  }

  /** Saldo dos itens de pedido usado pelos outros itens da nota, por item da nota (o backend soma por item do pedido). */
  private calcularUsos(): Map<number, UsoSaldoOutros> {
    return calcularUsoSaldoOutros(this.itens, this.estados, this.opcoes, this.nota?.idPessoaEmitente ?? null, this.tolerancias);
  }

  private calcularItem(item: VinculoPedidoNotaItemStruct, estado: EstadoItemVinculo): CalculoVinculoItem {
    return calcularVinculoItem(item, estado, this.opcoes, this.nota?.idPessoaEmitente ?? null, this.tolerancias,
      this.calcularUsos().get(item.idNFeEntradaItem)!);
  }

  /** Recalcula os view-models e os contadores do rodapé a partir do estado. */
  private recalcular(): void {
    const usos = this.calcularUsos();
    const todos = this.itens.map(item => montarItemVm(
      item,
      this.estados.get(item.idNFeEntradaItem) ?? estadoInicialItem(),
      this.opcoes,
      this.bloqueadas,
      this.flashId,
      this.buscandoId,
      this.nota?.idPessoaEmitente ?? null,
      this.nomesFornecedor,
      this.tolerancias,
      usos.get(item.idNFeEntradaItem)!,
    ));
    this.totalItens = todos.length;
    this.totalVinculados = todos.filter(vm => vm.resolvido).length;
    this.itensVm = todos.filter(vm =>
      this.filtro === 'pendentes' ? !vm.resolvido
        : this.filtro === 'vinculados' ? vm.resolvido
          : true);
  }
}
